/**
 * PptxExtractPanel — extracted Section B of MarpTemplateSettings.
 *
 * Drag-and-drop ``.pptx`` picker that calls
 * ``POST /api/v1/templates/marp/extract-from-pptx``; the BE sniffs the
 * brand colors + fonts and returns a Marp CSS preview. The user can
 * review the extracted tokens + CSS and click "Modifica nome e salva"
 * to drop the result back into Section A pre-filled (via the imperative
 * ``CssUploadPanelHandle`` ref injected by the parent shell).
 */

import { ArrowRight, FileUp, Loader2, Upload } from "lucide-react";
import * as React from "react";
import { useMutation } from "@tanstack/react-query";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";

import { Button, SettingsPanel, cn } from "@ando/ui";

import {
  extractCssFromPptx,
  type ExtractCssFromPptxResponse,
} from "@/features/sources/marp/api";

import type { CssUploadPanelHandle } from "./CssUploadPanel";
import { TokenSwatchTable } from "./shared";

const PPTX_MAX_BYTES = 20 * 1024 * 1024; // 20 MB ceiling (matches BE).
const ALLOWED_MIME = [
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
] as const;

interface ExtractedPreview {
  readonly tokens: Record<string, string>;
  readonly css: string;
  readonly suggestedSlug: string;
}

export interface PptxExtractPanelProps {
  /** Imperative handle to the sibling CssUploadPanel — used by
   *  "Modifica nome e salva" to lift the extracted CSS into Section A. */
  readonly cssPanelRef: React.MutableRefObject<CssUploadPanelHandle | null>;
}

export const PptxExtractPanel: React.FC<PptxExtractPanelProps> = ({
  cssPanelRef,
}) => {
  const { i18n } = useLingui();

  const [pptxFile, setPptxFile] = React.useState<File | null>(null);
  const [pptxError, setPptxError] = React.useState<string | null>(null);
  const [extracted, setExtracted] = React.useState<ExtractedPreview | null>(
    null,
  );

  const extractMutation = useMutation({
    mutationFn: (file: File) => extractCssFromPptx(file),
    onSuccess: (data: ExtractCssFromPptxResponse) => {
      setExtracted({
        tokens: data.tokens,
        css: data.css,
        suggestedSlug: data.suggested_slug,
      });
    },
    onError: (err) => {
      const reason =
        err instanceof Error ? err.message : i18n._(msg`Errore sconosciuto`);
      setPptxError(i18n._(msg`Estrazione fallita: ${reason}`));
    },
  });

  const handlePickPptx = React.useCallback(
    (file: File | null) => {
      setPptxError(null);
      setExtracted(null);
      if (!file) {
        setPptxFile(null);
        return;
      }
      if (file.size > PPTX_MAX_BYTES) {
        const sizeMb = (file.size / 1024 / 1024).toFixed(1);
        setPptxError(
          i18n._(msg`File troppo grande (${sizeMb} MB · max 20 MB)`),
        );
        setPptxFile(null);
        return;
      }
      if (
        !ALLOWED_MIME.includes(file.type as (typeof ALLOWED_MIME)[number]) &&
        !file.name.toLowerCase().endsWith(".pptx")
      ) {
        setPptxError(
          i18n._(msg`Formato non supportato — carica un file .pptx`),
        );
        setPptxFile(null);
        return;
      }
      setPptxFile(file);
    },
    [i18n],
  );

  const handleExtractSubmit = React.useCallback(() => {
    if (!pptxFile) return;
    extractMutation.mutate(pptxFile);
  }, [extractMutation, pptxFile]);

  /**
   * Lift the extracted CSS + suggested slug into Section A so the user
   * can finalize the name + description + scope before saving. We don't
   * auto-save — the user might want to tweak the CSS before persisting,
   * and the suggested slug usually needs a more human-friendly rename.
   */
  const handleAdoptExtraction = React.useCallback(() => {
    if (!extracted) return;
    cssPanelRef.current?.prefill(extracted.suggestedSlug, extracted.css);
  }, [extracted, cssPanelRef]);

  return (
    <SettingsPanel
      variant="card"
      aria-labelledby="marp-tpl-section-b-title"
      icon={FileUp}
      title={
        <span id="marp-tpl-section-b-title">
          <Trans>Importa da PowerPoint</Trans>
        </span>
      }
      description={
        <Trans>
          Carica un .pptx (max 20 MB) — l'AI ne estrae palette e tipografia.
        </Trans>
      }
    >
      <div className="flex flex-col gap-3">
        <label
          htmlFor="marp-tpl-pptx"
          className={cn(
            "flex h-32 cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed text-center transition-colors",
            pptxFile
              ? "border-ando-orange/50 bg-ando-orange-soft/30"
              : "border-hairline bg-card/40 hover:border-ando-orange/40 hover:bg-ando-orange-soft/20",
          )}
        >
          <Upload size={18} strokeWidth={1.75} className="text-ink-3" aria-hidden />
          <span className="font-medium text-[12px] text-ink-2">
            {pptxFile
              ? pptxFile.name
              : i18n._(msg`Trascina o seleziona un file .pptx`)}
          </span>
          {pptxFile ? (
            <span className="text-[10.5px] text-ink-3">
              {(pptxFile.size / 1024 / 1024).toFixed(2)} MB
            </span>
          ) : null}
          <input
            id="marp-tpl-pptx"
            type="file"
            accept=".pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation"
            className="sr-only"
            onChange={(e) => handlePickPptx(e.target.files?.[0] ?? null)}
          />
        </label>
        {pptxError ? (
          <p role="alert" className="text-[11px] text-destructive">
            {pptxError}
          </p>
        ) : null}

        <div className="flex items-center justify-end">
          <Button
            type="button"
            onClick={handleExtractSubmit}
            disabled={!pptxFile || extractMutation.isPending}
          >
            {extractMutation.isPending ? (
              <Loader2 size={14} strokeWidth={1.75} className="animate-spin" />
            ) : (
              <Upload size={14} strokeWidth={1.75} />
            )}
            {extractMutation.isPending
              ? i18n._(msg`Estrazione…`)
              : i18n._(msg`Estrai CSS`)}
          </Button>
        </div>

        {extracted ? (
          <div className="flex flex-col gap-3 border-hairline border-t pt-3">
            <div>
              <h3 className="mb-1.5 font-medium text-[11px] text-ink-3 uppercase tracking-wide">
                <Trans>Token rilevati</Trans>
              </h3>
              <TokenSwatchTable tokens={extracted.tokens} />
            </div>
            <div>
              <h3 className="mb-1.5 font-medium text-[11px] text-ink-3 uppercase tracking-wide">
                <Trans>CSS generato (anteprima)</Trans>
              </h3>
              <pre
                className="max-h-[200px] overflow-auto rounded-md border border-hairline bg-cream-deep/40 p-2.5 font-mono text-[10.5px] text-ink-2"
                aria-label={i18n._(msg`Anteprima CSS`)}
              >
                {extracted.css}
              </pre>
            </div>
            <div className="flex items-center justify-end">
              <Button
                type="button"
                variant="secondary"
                onClick={handleAdoptExtraction}
              >
                <Trans>Modifica nome e salva</Trans>
                <ArrowRight size={14} strokeWidth={1.75} />
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </SettingsPanel>
  );
};
PptxExtractPanel.displayName = "MarpTemplateSettings.PptxExtractPanel";
